import { peekPendingClaimToken, peekPendingInviteToken } from './pendingInvite';
import { apiFetch } from '../utils/api';

const ODIN_STORAGE_KEY = 'odin_state_v1';
const FRESH_ODIN_SESSION_KEY = 'halr_fresh_odin_login';
const PAID_SETUP_SESSION_KEY = 'halr_paid_setup_session';
const SKIP_FRESH_TRIAL_KEY = 'halr_skip_fresh_trial';

function shouldForceFreshTrialLogin(): boolean {
  if (typeof window !== 'undefined' && window.sessionStorage.getItem(SKIP_FRESH_TRIAL_KEY) === 'true') {
    return false;
  }
  if (typeof window !== 'undefined' && window.sessionStorage.getItem(PAID_SETUP_SESSION_KEY) === 'true') {
    return false;
  }
  const raw = (import.meta as any).env?.VITE_FORCE_FRESH_TRIAL_LOGIN;
  if (raw === 'false') return false;
  return (import.meta as any).env?.DEV === true || raw === 'true';
}

export function markPaidSetupSession(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(PAID_SETUP_SESSION_KEY, 'true');
  window.sessionStorage.setItem(SKIP_FRESH_TRIAL_KEY, 'true');
  window.sessionStorage.removeItem(FRESH_ODIN_SESSION_KEY);
}

export function consumePaidSetupSessionFlag(): boolean {
  if (typeof window === 'undefined') return false;
  const value = window.sessionStorage.getItem(PAID_SETUP_SESSION_KEY) === 'true';
  if (value) window.sessionStorage.removeItem(PAID_SETUP_SESSION_KEY);
  return value;
}

export function consumeFreshOdinLoginFlag(): boolean {
  if (typeof window === 'undefined') return false;
  const value = window.sessionStorage.getItem(FRESH_ODIN_SESSION_KEY) === 'true';
  if (value) window.sessionStorage.removeItem(FRESH_ODIN_SESSION_KEY);
  return value;
}

export function isFreshOdinLoginSession(): boolean {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(FRESH_ODIN_SESSION_KEY) === 'true';
}

/**
 * Dev helper for brand-new accounts only: clear Odin draft + seed a clean trial.
 * Returning users (any subscription access other than none) must NOT get
 * halr_fresh_odin_login — that re-forced Welcome on every local login.
 * Must NEVER overwrite expired / canceled / paid states.
 */
export async function prepareFreshTrialLogin(): Promise<void> {
  if (!shouldForceFreshTrialLogin() || typeof window === 'undefined') return;
  // Joining a paid workspace is not a fresh trial signup.
  if (peekPendingInviteToken() || peekPendingClaimToken()) return;

  try {
    const statusRes = await apiFetch('/api/subscription/status');
    if (statusRes.ok) {
      const status = await statusRes.json().catch(() => null);
      const access = status?.access as string | undefined;
      // Returning users: leave Odin session + trial state alone.
      if (access && access !== 'none') {
        window.sessionStorage.removeItem(FRESH_ODIN_SESSION_KEY);
        return;
      }
    }

    window.localStorage.removeItem(ODIN_STORAGE_KEY);
    window.sessionStorage.removeItem('trial_status_banner_dismissed');
    window.sessionStorage.removeItem('trial_banner_dismissed');
    window.sessionStorage.setItem(FRESH_ODIN_SESSION_KEY, 'true');

    await apiFetch('/api/subscription/dev/simulate', {
      method: 'POST',
      body: { state: 'trialing', daysRemaining: 14 },
    });
  } catch (err) {
    console.warn('[DEV-FRESH-LOGIN] Failed to reset trial simulation', err);
  }
}
